import { Button, DataTable, ResultsCard, ResultsToolbar } from 'shared/components/ui';
import React, { useState, useCallback } from 'react';
import { EventNote, UnfoldMore } from '@mui/icons-material';

import { Search, Clear } from '@mui/icons-material';
import Header from '../../../shared/components/layout/Header';
import Footer from '../../../shared/components/layout/Footer';
import { Toast } from '../../../shared/components/Toast';
import { turnosAPI } from '../../../core/api/api';
import { centrosAPI } from '../../../core/api/api';
import { useSortableTable } from '../../../shared/hooks/useSortableTable';
import '../../turnos/components/GrillaTurnos.css';
import '../../practicas/components/GrillaDatosPracticas.css';
import '../../../styles/buttons.css';

const PAGE_SIZE = 50;

const hoy = () => new Date().toISOString().split('T')[0];

const GrillaTurnosCreados = () => {
  const [turnos, setTurnos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const [fecha, setFecha] = useState(hoy());
  const [idCentro, setIdCentro] = useState(-1);
  const [centros, setCentros] = useState([]);
  const [page, setPage] = useState(1);
  const { sortedData, requestSort, getSortIcon } = useSortableTable(turnos);

  const cargarCentros = useCallback(async () => {
    try {
      const res = await centrosAPI.getCentros();
      setCentros(res.data || []);
    } catch {
      // silencioso
    }
  }, []);

  React.useEffect(() => { cargarCentros(); }, [cargarCentros]);

  const buscar = useCallback(async () => {
    if (!fecha) return;
    setLoading(true);
    try {
      const res = await turnosAPI.getTurnosCreados({
        FechaAlta: fecha,
        IdCentro: Number(idCentro),
      });
      setTurnos(res.data || []);
      setPage(1);
    } catch {
      setToast({ message: 'Error al cargar los turnos.', type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [fecha, idCentro]);

  const limpiar = () => {
    setFecha(hoy());
    setIdCentro(-1);
    setTurnos([]);
    setPage(1);
  };

  const totalPages = Math.ceil(sortedData.length / PAGE_SIZE);
  const paginados = sortedData.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const totalesPorUsuario = sortedData.reduce((acc, t) => {
    const usuario = t.usrAlta || 'Sin usuario';
    acc[usuario] = (acc[usuario] || 0) + 1;
    return acc;
  }, {});

  const estadoColor = (estado) => {
    if (!estado) return {};
    const e = estado.toLowerCase();
    if (e === 'cancelado' || e === 'no realizado') return { backgroundColor: '#dc3545', color: '#fff' };
    if (e === 'realizado') return { backgroundColor: '#198754', color: '#fff' };
    if (e === 'confirmado') return { backgroundColor: '#0d6efd', color: '#fff' };
    return {};
  };

  return (
    <div className="grilla-turnos">
      <Header />
      <div className="main-content">
        <div className="page-header">
          <h2 className="page-title-grilla">
            <EventNote sx={{ fontSize: 32, mr: 1, verticalAlign: 'middle', color: '#6d1212' }} />
            Turnos del Día
          </h2>
          <span className="page-subtitle">Turnos creados en el día seleccionado</span>
        </div>

        <div className="filters-card">
          <div className="filters-header">
            <h3 className="filters-title">Filtros de Búsqueda</h3>
          </div>
          <div className="filters-content">
            <div className="filters-row">
              <div className={`filter-group ${fecha ? 'filter-active' : ''}`}>
                <label className="filter-label">Fecha de creación</label>
                <input
                  type="date"
                  className="filter-input"
                  value={fecha}
                  onChange={(e) => setFecha(e.target.value)}
                />
              </div>
              <div className={`filter-group ${idCentro !== -1 ? 'filter-active' : ''}`}>
                <label className="filter-label">Centro</label>
                <select
                  className="filter-input"
                  value={idCentro}
                  onChange={(e) => setIdCentro(e.target.value)}
                >
                  <option value={-1}>Todos</option>
                  {centros.map((c) => (
                    <option key={c.idCentro} value={c.idCentro}>{c.centro}</option>
                  ))}
                </select>
              </div>
              <div className="filter-actions">
                <Button className="btn-search" onClick={buscar} disabled={loading || !fecha}>
                  <Search sx={{ fontSize: 18, mr: 0.5 }} /> Buscar
                </Button>
                <Button className="btn-clear" onClick={limpiar}>
                  <Clear sx={{ fontSize: 18, mr: 0.5 }} /> Limpiar
                </Button>
              </div>
            </div>
          </div>
        </div>

        <ResultsCard className="results-card">
          <ResultsToolbar className="results-header">
            <div className="results-header-left">
              <h3 className="results-title">Resultados</h3>
              <div className="estado-badges">
                {Object.entries(totalesPorUsuario).map(([usuario, cantidad]) => (
                  <span key={usuario} className="estado-count estado-count-primary">
                    {cantidad} {usuario}
                  </span>
                ))}
              </div>
            </div>
            <div className="results-info">
              <span className="results-count">{sortedData.length} turnos encontrados</span>
              {loading && <span className="loading-indicator">Cargando...</span>}
            </div>
          </ResultsToolbar>
          <div className="table-container table-container-scrollable">
            <DataTable className="data-table">
              <thead>
                <tr>
                  <th onClick={() => requestSort('idTurno')} style={{ cursor: 'pointer' }}>
                    # {getSortIcon('idTurno') || <UnfoldMore sx={{ fontSize: 14, opacity: 0.4 }} />}
                  </th>
                  <th onClick={() => requestSort('numero')} style={{ cursor: 'pointer' }}>
                    Nro {getSortIcon('numero') || <UnfoldMore sx={{ fontSize: 14, opacity: 0.4 }} />}
                  </th>
                  <th onClick={() => requestSort('persona.apellido')} style={{ cursor: 'pointer' }}>
                    Paciente {getSortIcon('persona.apellido') || <UnfoldMore sx={{ fontSize: 14, opacity: 0.4 }} />}
                  </th>
                  <th>Teléfono</th>
                  <th onClick={() => requestSort('tipoEstudio.tipoEstudio')} style={{ cursor: 'pointer' }}>
                    Estudio {getSortIcon('tipoEstudio.tipoEstudio') || <UnfoldMore sx={{ fontSize: 14, opacity: 0.4 }} />}
                  </th>
                  <th onClick={() => requestSort('horario.inicio')} style={{ cursor: 'pointer' }}>
                    Fecha Turno {getSortIcon('horario.inicio') || <UnfoldMore sx={{ fontSize: 14, opacity: 0.4 }} />}
                  </th>
                  <th>Obra Social</th>
                  <th>Req</th>
                  <th onClick={() => requestSort('estadosTurno.estadoTurno')} style={{ cursor: 'pointer' }}>
                    Estado {getSortIcon('estadosTurno.estadoTurno') || <UnfoldMore sx={{ fontSize: 14, opacity: 0.4 }} />}
                  </th>
                  <th onClick={() => requestSort('usrAlta')} style={{ cursor: 'pointer' }}>
                    Otorgado por {getSortIcon('usrAlta') || <UnfoldMore sx={{ fontSize: 14, opacity: 0.4 }} />}
                  </th>
                </tr>
              </thead>
              <tbody>
                {paginados.length === 0 ? (
                  <tr>
                    <td colSpan="10" className="empty-state">
                      {loading ? 'Cargando...' : 'Ingrese una fecha y presione Buscar'}
                    </td>
                  </tr>
                ) : (
                  paginados.map((t) => (
                    <tr key={t.idTurno}>
                      <td>{t.idTurno}</td>
                      <td>{t.numero}</td>
                      <td>{t.persona ? `${t.persona.apellido}, ${t.persona.nombre}` : '-'}</td>
                      <td>{t.persona?.telCelular || '-'}</td>
                      <td>{t.tipoEstudio?.tipoEstudio || '-'}</td>
                      <td>
                        {t.horario?.inicio
                          ? new Date(t.horario.inicio).toLocaleDateString('es-AR') + ' ' +
                            new Date(t.horario.inicio).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' })
                          : '-'}
                      </td>
                      <td>{t.persona?.obraSocial?.obraSocial || '-'}</td>
                      <td>{t.requerimientos || '-'}</td>
                      <td style={estadoColor(t.estadosTurno?.estadoTurno)}>
                        {t.estadosTurno?.estadoTurno || '-'}
                      </td>
                      <td>{t.usrAlta || '-'}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </DataTable>
          </div>

          {totalPages > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '0.5rem', padding: '1rem' }}>
              <Button variant="outline-secondary" size="sm" onClick={() => setPage(1)} disabled={page === 1}>«</Button>
              <Button variant="outline-secondary" size="sm" onClick={() => setPage(p => p - 1)} disabled={page === 1}>‹</Button>
              <span style={{ fontSize: '0.875rem', color: '#64748b' }}>Página {page} de {totalPages}</span>
              <Button variant="outline-secondary" size="sm" onClick={() => setPage(p => p + 1)} disabled={page === totalPages}>›</Button>
              <Button variant="outline-secondary" size="sm" onClick={() => setPage(totalPages)} disabled={page === totalPages}>»</Button>
            </div>
          )}
        </ResultsCard>
      </div>
      <Footer />
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
};

export default GrillaTurnosCreados;
